import { useCallback, useEffect, useMemo, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import { createWorkspaceClient } from '../lib/workspaceClient';

function normalizeTask(task) {
  return {
    ...task,
    id: task.id || task.taskId || task._id,
    scope: task.scope || (task.scopeType === 'family' ? 'family' : 'personal'),
  };
}

export function useTasks(currentUser) {
  const workspaceClient = useMemo(() => createWorkspaceClient(getBackendUrl()), []);
  const [tasks, setTasks] = useState([]);
  const [taskFilters, setTaskFilters] = useState({ status: 'all', assignedTo: '' });
  const [taskError, setTaskError] = useState(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  const resolveTaskScope = useCallback(
    (taskId, fallback = 'personal') => tasks.find((task) => task.id === taskId)?.scope || fallback,
    [tasks]
  );

  const loadTasks = useCallback(async () => {
    if (!currentUser?.userId) {
      setTasks([]);
      return;
    }

    setTaskError(null);
    setIsLoadingTasks(true);

    try {
      const response = await workspaceClient.getTasks(currentUser.userId, {
        status: taskFilters.status,
        assignedTo: taskFilters.assignedTo,
      });
      const fetchedTasks = Array.isArray(response?.tasks) ? response.tasks : response;
      setTasks((Array.isArray(fetchedTasks) ? fetchedTasks : []).map(normalizeTask));
    } catch (error) {
      setTaskError(error?.error || error?.message || 'Could not load tasks.');
    } finally {
      setIsLoadingTasks(false);
    }
  }, [currentUser?.userId, taskFilters, workspaceClient]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  const refreshTasks = useCallback(async () => {
    await loadTasks();
  }, [loadTasks]);

  const createTask = useCallback(
    async (task, options = {}) => {
      if (!currentUser?.userId) return;
      const scope = task?.scope === 'family' ? 'family' : 'personal';

      if (scope === 'family' && options.canUseFamilyScope === false) {
        setTaskError('Create or join a family before adding family tasks.');
        return false;
      }

      setTaskError(null);
      setIsLoadingTasks(true);
      try {
        await workspaceClient.createTask(currentUser.userId, task);
        await loadTasks();
        return true;
      } catch (error) {
        setTaskError(error?.error || error?.message || 'Could not create task.');
        return false;
      } finally {
        setIsLoadingTasks(false);
      }
    },
    [currentUser?.userId, loadTasks, workspaceClient]
  );

  const updateTask = useCallback(
    async (taskId, updates) => {
      if (!currentUser?.userId) return;
      setTaskError(null);
      setIsLoadingTasks(true);
      try {
        await workspaceClient.updateTask(currentUser.userId, taskId, updates, {
          scope: resolveTaskScope(taskId),
        });
        await loadTasks();
        return true;
      } catch (error) {
        setTaskError(error?.error || error?.message || 'Could not update task.');
        return false;
      } finally {
        setIsLoadingTasks(false);
      }
    },
    [currentUser?.userId, loadTasks, resolveTaskScope, workspaceClient]
  );

  const completeTask = useCallback(
    async (taskId) => {
      if (!currentUser?.userId) return;
      setTaskError(null);
      setIsLoadingTasks(true);
      try {
        await workspaceClient.completeTask(currentUser.userId, taskId, {
          scope: resolveTaskScope(taskId),
        });
        await loadTasks();
        return true;
      } catch (error) {
        setTaskError(error?.error || error?.message || 'Could not complete task.');
        return false;
      } finally {
        setIsLoadingTasks(false);
      }
    },
    [currentUser?.userId, loadTasks, resolveTaskScope, workspaceClient]
  );

  const deleteTask = useCallback(
    async (taskId) => {
      if (!currentUser?.userId) return false;
      setTaskError(null);
      setIsLoadingTasks(true);
      try {
        await workspaceClient.deleteTask(currentUser.userId, taskId, {
          scope: resolveTaskScope(taskId),
        });
        await loadTasks();
        return true;
      } catch (error) {
        setTaskError(error?.error || error?.message || 'Could not delete task.');
        return false;
      } finally {
        setIsLoadingTasks(false);
      }
    },
    [currentUser?.userId, loadTasks, resolveTaskScope, workspaceClient]
  );

  const canShowEmptyState = useMemo(
    () => !isLoadingTasks && !tasks.length && !taskError,
    [isLoadingTasks, tasks.length, taskError]
  );

  return {
    tasks,
    taskFilters,
    taskError,
    isLoadingTasks,
    canShowEmptyState,
    refreshTasks,
    createTask,
    updateTask,
    completeTask,
    deleteTask,
    setTaskFilters,
    hasTasks: tasks.length > 0,
  };
}
